import { Component, signal, computed, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterLink, Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../core/services/auth/auth.service';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal/confirmation-modal.component';
import { TelecallerVerificationModalComponent } from '../../shared/components/telecaller-verification-modal/telecaller-verification-modal.component';
import { AvatarComponent } from '../../shared/components/avatar/avatar.component';
import { filter, map } from 'rxjs/operators';
import { Subscription, interval } from 'rxjs';
import { SidebarComponent } from '../../shared/components/sidebar/sidebar.component';
import { UserRole } from '../../core/models/role.enum';
import { NotificationDrawerComponent } from '../../shared/components/notification-drawer/notification-drawer.component';
import { MessagingService } from '../../core/services/notifications/messaging.service';
import { BreadcrumbService } from '../../core/services/breadcrumb/breadcrumb.service';
import { TelecallersApiService } from '../../core/services/telecallers/telecallers-api.service';

interface Breadcrumb {
  label: string;
  url: string;
  isLast: boolean;
}

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, ConfirmationModalComponent, TelecallerVerificationModalComponent, AvatarComponent, SidebarComponent, NotificationDrawerComponent],
  templateUrl: './app-layout.component.html',
  styleUrls: ['./app-layout.component.css'],
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  collapsed = false;
  breadcrumbs: Breadcrumb[] = [];
  private routerSubscription?: Subscription;
  private breadcrumbOverridesSubscription?: Subscription;

  // User info
  userName = '';
  userRoleLabel = '';
  avatarUrl = computed(() => this.authService.currentUser()?.avatar ?? '');

  // Header profile dropdown
  isProfileMenuOpen = false;

  // Notification drawer
  showNotificationDrawer = false;

  // Logout modal
  showLogoutModal = false;

  mobileSidebarOpen = false;

  // Telecaller phone verification freeze modal
  showVerificationModal = false;
  isCheckingVerification = false;
  verificationErrorMessage: string | null = null;
  verificationExotelNumber: string | null = null;
  private telecallerId: string | null = null;

  // Inactive telecaller account freeze modal (auto-logout countdown)
  showInactiveAccountModal = false;
  inactiveAccountCountdown = 30;
  private inactiveAccountTimerSub?: Subscription;

  private routeTitleMap: { [key: string]: string } = {
    dashboard: 'Dashboard',
    'patient-list': 'Patient List',
    patient: 'Patient List',
    'patient-report': 'Patient Report',
    telecaller: 'Telecaller Management',
    'telecaller-detail': 'Telecaller Detail',
    doctors: 'Doctor List',
    labs: 'Labs',
    'lab-test-bookings': 'Lab Test Bookings',
    hospitals: 'Hospitals',
    'call-logs': 'Call Logs',
    employees: 'Employees',
    organization: 'Organization',
    'create-organization': 'Create Organization',
    'add-user': 'Add User',
    'add-program': 'Add Program',
    profile: 'Profile',
    calls: 'Calls',
    inventory: 'Inventory',
    medicines: 'Medicines',
    suppliers: 'Suppliers',
    stock: 'Stock',
    batches: 'Batches',
    alerts: 'Alerts',
    reports: 'Reports',
    coupons: 'Coupons',
    orders: 'Orders',
    'medicine-orders': 'Medicine Orders',
    'lab-test-orders': 'Lab Test Booking',
    hq: 'HQ',
  };

  constructor(
    private router: Router,
    private authService: AuthService,
    private messagingService: MessagingService,
    private breadcrumbService: BreadcrumbService,
    private telecallersApiService: TelecallersApiService,
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    if (user) {
      this.userName = user.name;
      this.userRoleLabel = user.roleName || this.formatRole(user.role);

      if (user.is_telecaller) {
        this.handleTelecallerAccessCheck();
      }
    }

    this.messagingService.requestPermissionAndGetToken();
    this.generateBreadcrumbs();

    this.routerSubscription = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), map(() => this.generateBreadcrumbs()))
      .subscribe();

    this.breadcrumbOverridesSubscription = this.breadcrumbService.overrides$.subscribe(() => {
      this.generateBreadcrumbs();
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.breadcrumbOverridesSubscription?.unsubscribe();
    this.inactiveAccountTimerSub?.unsubscribe();
  }

  // ── Desktop & Mobile sidebar ─────────────────────────────────────
  toggleSidebar()      { this.collapsed = !this.collapsed; }
  openMobileSidebar()  { this.mobileSidebarOpen = true; }
  closeMobileSidebar() { this.mobileSidebarOpen = false; }

  // ── Profile dropdown ──────────────────────────────────────────────
  toggleProfileMenu() { this.isProfileMenuOpen = !this.isProfileMenuOpen; }
  closeProfileMenu()  { this.isProfileMenuOpen = false; }

  // ── Logout ────────────────────────────────────────────────────────
  openLogoutModal():   void { this.isProfileMenuOpen = false; this.showLogoutModal = true; }
  closeLogoutModal():  void { this.showLogoutModal = false; }
  confirmLogout():     void { this.showLogoutModal = false; this.authService.logout(); }

  // ── Notifications ─────────────────────────────────────────────────
  openNotificationDrawer():  void { this.showNotificationDrawer = true; }
  closeNotificationDrawer(): void { this.showNotificationDrawer = false; }

  // ── Telecaller phone verification ───────────────────────────────────
  private handleTelecallerAccessCheck(): void {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return;
    }

    if ((user.telecaller_status || '').toLowerCase() !== 'active') {
      // Deactivated telecaller: show a freeze message, then auto-logout
      this.startInactiveAccountCountdown();
      return;
    }

    this.telecallerId = user.id;
    this.verificationExotelNumber = user.exotel_number || null;
    this.checkTelecallerVerification();
  }

  private startInactiveAccountCountdown(): void {
    this.showInactiveAccountModal = true;
    this.inactiveAccountCountdown = 30;
    this.inactiveAccountTimerSub?.unsubscribe();
    this.inactiveAccountTimerSub = interval(1000).subscribe(() => {
      this.inactiveAccountCountdown--;
      if (this.inactiveAccountCountdown <= 0) {
        this.inactiveAccountTimerSub?.unsubscribe();
        this.authService.logout();
      }
    });
  }

  private checkTelecallerVerification(showErrorOnFailure: boolean = false): void {
    if (!this.telecallerId || this.isCheckingVerification) {
      return;
    }
    this.isCheckingVerification = true;
    this.telecallersApiService.getVerificationStatus(this.telecallerId).subscribe({
      next: (response) => {
        this.isCheckingVerification = false;
        const verified = !!(response?.verified || response?.status?.toLowerCase() === 'verified');
        this.showVerificationModal = !verified;
        this.verificationErrorMessage = verified || !showErrorOnFailure
          ? null
          : 'Your number is still not verified. Please complete the steps above and try again.';
      },
      error: () => {
        this.isCheckingVerification = false;
        // Fail closed: keep the freeze modal up if we can't confirm verification
        this.showVerificationModal = true;
        this.verificationErrorMessage = showErrorOnFailure
          ? 'Could not check verification status. Please try again.'
          : null;
      },
    });
  }

  onCheckVerification(): void {
    this.checkTelecallerVerification(true);
  }

  // ── Breadcrumbs ───────────────────────────────────────────────────
  private generateBreadcrumbs(): void {
    const urlSegments = this.router.url.split('/').filter(s => s);
    this.breadcrumbs = [];
    let currentPath = '';

    urlSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === urlSegments.length - 1;
      
      // Clean segment by removing query parameters for matching overrides and titles
      const cleanSegment = segment.split('?')[0];
      
      // Parse query parameters from segment if present
      let queryParams: { [key: string]: string } = {};
      if (segment.includes('?')) {
        const queryStr = segment.split('?')[1];
        queryStr.split('&').forEach(pair => {
          const [key, val] = pair.split('=');
          if (key && val) {
            queryParams[key] = decodeURIComponent(val);
          }
        });
      }

      // Resolve override: check full segment, clean segment, and query param ID values
      let overrideLabel = this.breadcrumbService.getOverrideLabel(segment) || 
                          this.breadcrumbService.getOverrideLabel(cleanSegment);
      
      if (!overrideLabel) {
        for (const paramKey of ['id', 'patientId', 'doctorId', 'hospitalId', 'labId']) {
          if (queryParams[paramKey]) {
            const val = queryParams[paramKey];
            const valOverride = this.breadcrumbService.getOverrideLabel(val);
            if (valOverride) {
              overrideLabel = valOverride;
              break;
            }
          }
        }
      }

      const skipSegments = ['telecaller-detail', 'doctor-detail', 'hospital-detail', 'lab-detail'];
      if (skipSegments.includes(cleanSegment)) {
        return;
      }

      const label = overrideLabel || this.routeTitleMap[cleanSegment] || this.formatSegment(cleanSegment);
      this.breadcrumbs.push({ label, url: currentPath, isLast });
    });
  }

  private formatSegment(segment: string): string {
    return segment.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  private formatRole(role: UserRole | string): string {
    return String(role).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
}
